import { redactValue } from './google-analytics.pii';

/**
 * Character and count limits enforced by Google Analytics 4.
 */
export const GOOGLE_ANALYTICS_LIMITS = {
    /** Maximum length of a parameter name (e.g. "page_title"). */
    PARAMETER_NAME_MAX_LENGTH: 40,

    /** Default maximum length for parameter values. */
    PARAMETER_VALUE_MAX_LENGTH: 100,

    /** Maximum custom parameters per event (excludes automatic parameters). */
    PARAMETERS_PER_EVENT_MAX: 25,

    /** Maximum length of the event name itself. */
    EVENT_NAME_MAX_LENGTH: 40,

    /** Maximum length of a user ID. */
    USER_ID_MAX_LENGTH: 256,

    /** Maximum length of a user property name. */
    USER_PROPERTY_NAME_MAX_LENGTH: 24,

    /** Maximum length of a user property value. */
    USER_PROPERTY_VALUE_MAX_LENGTH: 36,
} as const;

/** Per-parameter value length overrides for the page-scoped parameters GA4 treats specially. */
export const GOOGLE_ANALYTICS_PAGE_PARAM_VALUE_LIMITS: Readonly<Record<string, number>> = {
    page_location: 1000,
    page_referrer: 420,
    page_title: 300,
};

/**
 * Parameters whose meaning is scoped to the current page rather than to a single event.
 *
 * These are held as sticky context so every event fired on a page carries them, then cleared when the next
 * `page_view` fires. Without that reset, stale page context would leak forward and mislabel events on later pages.
 */
export const GOOGLE_ANALYTICS_PAGE_SCOPED_PARAMS: Readonly<string[]> = [
    'page_location',
    'page_referrer',
    'page_title',
    'module_name',
];

/** Valid GA4 event and parameter name pattern: starts with a letter, then letters/digits/underscores. */
export const GOOGLE_ANALYTICS_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;

/** Parameter name prefixes GA4 reserves; these will be silently dropped. */
export const GOOGLE_ANALYTICS_RESERVED_PARAM_PREFIXES = ['_', 'firebase_', 'ga_', 'google_', 'gtag.'];

/**
 * Signature every entry in the parameter allow-list must satisfy: a pure string-to-string transform applied to a
 * parameter value just before it is sent to GA4.
 *
 * Modeling the allow-list values as transforms (rather than as booleans or plain names) lets each parameter declare
 * its own normalization policy in one place, so callers never have to remember which values need reshaping.
 */
type StringTransform = (value: string) => string;

/**
 * Sends the value to GA4 exactly as supplied.
 */
export function passthrough(value: string): string {
    return value;
}

/**
 * Normalizes a value to lower snake_case: lowercased, non-alphanumeric runs collapsed to single underscores, and
 * leading/trailing underscores stripped.
 *
 * Used for parameters whose values become GA4 report dimensions, where free-form casing and punctuation would
 * otherwise split one logical value across several report rows.
 *
 * @param value - the raw parameter value
 * @returns the snake_cased value
 *
 * @example
 * toSnakeCase('Service Manual'); // 'service_manual'
 * toSnakeCase('  NVH / Powertrain  '); // 'nvh_powertrain'
 */
export function toSnakeCase(value: string): string {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

/**
 * Normalizes a comma-separated list of values to lower snake_case.
 *
 * @param value - the raw comma-separated list
 * @returns the normalized comma-separated list
 */
export function toSnakeCaseList(value: string): string {
    return value.split(',').map(toSnakeCase).filter(Boolean).sort().join(',');
}

/**
 * GA4 Event parameter naming and value-transform rules.
 *
 * This is an ALLOW-LIST, and that is the single most surprising thing about this module: a parameter whose name is
 * not a key here is silently dropped during payload construction (with a console warning), no matter what the caller
 * passed. Analytics parameters are effectively permanent once they reach GA4, so new parameters are opted in here
 * deliberately rather than flowing through by accident.
 *
 * Each key maps to the {@link StringTransform} applied to that parameter's value before it is sent, so a parameter's
 * name and its normalization policy are declared together in one place.
 */
export const GOOGLE_ANALYTICS_PARAMS = {
    /** Page Scope Params */
    page_location: passthrough,
    page_referrer: passthrough,
    page_title: passthrough,
    module_name: toSnakeCase,

    filter_count: passthrough,
    filters_applied: toSnakeCaseList,
    entry_point: toSnakeCase,
    search_type: toSnakeCase,

    /** Generic Params */
    duration_ms: passthrough,
    end_reason: toSnakeCase,
    error_type: toSnakeCase,
    open_mode: toSnakeCase,
    results_count: passthrough,
    search_term: redactValue,
    success: passthrough,
    error_code: toSnakeCase,

    /** STIS Specific Params */
    document_id: passthrough,
    document_type: toSnakeCase,

    /** QMR Specific Params */
    report_status: toSnakeCase,
    report_type: toSnakeCase,
    attachment_count: passthrough,
    dtc_count: passthrough,
    fail_code: toSnakeCase,

    /** CIDCVN Specific Params */
    result: toSnakeCase,

    /** Techline Specific Params */
    case_status: toSnakeCase,
    case_type: toSnakeCase,

    /** Worksheet Specific Params */
    worksheet_group: toSnakeCase,

    /** Vehicle Params */
    model: toSnakeCase,
    model_year: passthrough,
    model_number: toSnakeCase,
} as const satisfies Record<string, StringTransform>;

/**
 * Every parameter name GA4 will accept from this application.
 *
 * Derived from the keys of {@link GOOGLE_ANALYTICS_PARAMS} rather than written out by hand, so adding a key to the
 * allow-list automatically widens this type; the runtime allow-list and the compile-time type can never drift apart.
 */
export type GoogleAnalyticsParamName = keyof typeof GOOGLE_ANALYTICS_PARAMS;

/**
 * Type guard reporting whether a parameter name appears in the {@link GOOGLE_ANALYTICS_PARAMS} allow-list.
 *
 * Narrowing to {@link GoogleAnalyticsParamName} is what lets callers index the allow-list for a value transform
 * without a cast; a name that fails this check has no transform and is dropped from the payload.
 *
 * @param name - the candidate parameter name
 * @returns true when the name is an allowed parameter, narrowing it to {@link GoogleAnalyticsParamName}
 *
 * @example
 * if (isAllowedParameterName(name)) {
 *     const transformed = GOOGLE_ANALYTICS_PARAMS[name](value);
 * }
 */
export function isAllowedParameterName(name: string): name is GoogleAnalyticsParamName {
    return Object.prototype.hasOwnProperty.call(GOOGLE_ANALYTICS_PARAMS, name);
}

/**
 * Caps a string at a maximum length, returning it unchanged when it already fits.
 */
export function truncate(value: string, maxLength: number): string {
    if (value.length <= maxLength) {
        return value;
    }
    return value.slice(0, maxLength);
}
